import { useMemo, useRef } from "react";
import classNames from "classnames";
import withHeaderItem from "@/utils/hoc/withHeaderItem";
import type { DropdownRef } from "@/components/ui/Dropdown";
import Dropdown from "@/components/ui/Dropdown";
import ScrollBar from "@/components/ui/ScrollBar";
import Badge from "@/components/ui/Badge";
import NotificationAvatar from "./NotificationAvatar";
import NotificationToggle from "./NotificationToggle";
import useResponsive from "@/utils/hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import {
  AdmUserNotificationsApi,
  AdmUserNotificationsApi$LatestNotification,
  AdmUserNotificationsApi$LatestNotificationType,
} from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";
import { useAtom } from "jotai";
import { adminAreaNotifications } from "@/custolink-state/ui_admin_area_notifications";
import { useMutation } from "@tanstack/react-query";
import { Tooltip } from "@/components/ui/Tooltip";
import Button from "@/components/ui/Button/Button";
import { HiOutlineMailOpen } from "react-icons/hi";

const notificationHeight = "h-[280px]";

const _Notification = ({ className }: { className?: string }) => {

  const [notifications,setNotifications] = useAtom(adminAreaNotifications)

  const markAsRead = useMutation({
    mutationFn: (p:{notificationId:number}) => AdmUserNotificationsApi.markAsRead(p.notificationId),
    onSuccess:(_,p:{notificationId:number})=>{
      setNotifications(s => {
        return {...s, latest10: s.latest10.map(s => s.id === p.notificationId ? {...s, isRead: true} : s)}
      })
    }
  })

  const markAllAsRead = useMutation({
    mutationFn: () => AdmUserNotificationsApi.markMultipleAsRead(
      {ids:notifications.latest10.map(s=>s.id)}),
    onSuccess:()=>{
      setNotifications(s => {
        const latest10 = [...s.latest10];
        latest10.forEach(s=>s.isRead = true)
        return {...s, latest10: s.latest10}
      })
    }
  })

  //const navigate = useNavigate();

  //const [unreadNotification, setUnreadNotification] = useState(false);

  const { larger } = useResponsive();

  const notificationDropdownRef = useRef<DropdownRef>(null);

//  const handleViewAllActivity = () => {
    //navigate("/concepts/account/activity-log");
    //if (notificationDropdownRef.current) {
//      notificationDropdownRef.current.handleDropdownClose();
    //  }
  //};

  const hasUnread = useMemo(()=>{
    return notifications.latest10.find(s => !s.isRead) != null
  },[notifications])

  return (
    <Dropdown
      ref={notificationDropdownRef}
      renderTitle={<NotificationToggle dot={hasUnread}
                                       className={className} />}
      menuClass="min-w-[280px] md:min-w-[340px]"
      placement={larger.md ? "bottom-end" : "bottom"}
      onOpen={() => {}}
    >
      <Dropdown.Item variant="header">
        <div className="dark:border-gray-700 px-2 flex items-center justify-between mb-1">
          <h6>Notifications</h6>
          {hasUnread && <Tooltip title="Mark all as read">
            <Button
              variant="plain"
              shape="circle"
              size="sm"
              icon={<HiOutlineMailOpen className="text-xl" />}
              onClick={()=>markAllAsRead.mutate()}
            />
          </Tooltip>}
        </div>
      </Dropdown.Item>
      <ScrollBar className={classNames("overflow-y-auto", notificationHeight)}>
        {notifications.latest10.length > 0 &&
           notifications.latest10.map((item, index) => (
             <NotificationEntry
               key={item.id}
               last={index == notifications.latest10.length - 1}
               item={item}
               markAsRead={()=>markAsRead.mutate({notificationId: item.id})}
             />
           ))}

        {notifications.latest10.length === 0 && (
          <div
            className={classNames(
              "flex items-center justify-center",
              notificationHeight,
            )}
          >
            <div className="text-center">
              <h6 className="font-semibold">No notifications!</h6>
              <p className="mt-1">Please Try again later</p>
            </div>
          </div>
        )}
      </ScrollBar>
      {/*<Dropdown.Item variant="header">
        <div className="pt-4">
          <Button block variant="solid" onClick={handleViewAllActivity}>
            View All Activity
          </Button>
        </div>
      </Dropdown.Item>*/}
    </Dropdown>
  );
};

function NotificationEntry(p: {
  last: boolean;
  item: AdmUserNotificationsApi$LatestNotification;
  markAsRead?: (id: number) => void;
}) {
  const navigate = useNavigate();
  let to = "";
  switch (p.item.type) {
    case AdmUserNotificationsApi$LatestNotificationType.AssetAiConversation:
    case AdmUserNotificationsApi$LatestNotificationType.AssetVideoCall:
    case AdmUserNotificationsApi$LatestNotificationType.Inquiry:
    case AdmUserNotificationsApi$LatestNotificationType.ProductRegistered:
      to = "/admin/communication/support";
      break;
  }

  return (
    <div key={p.item.id}>
      <div
        className={`relative rounded-xl flex px-4 py-3 cursor-pointer hover:bg-gray-100 active:bg-gray-100 dark:hover:bg-gray-700`}
        onClick={() => {
          // mark as read on click, then navigate
          p.markAsRead?.(p.item.id);
          navigate(to);
        }}
      >
        <div>
          <NotificationAvatar type={p.item.type} />
        </div>
        <div className="mx-3">
          {
            <TextOfType type={p.item.type}/>
          }
          <span className="text-xs">{p.item.createdPrettyDate}</span>
        </div>
        <Badge
          className="absolute top-4 ltr:right-4 rtl:left-4 mt-1.5"
          innerClass={`${
            p.item.isRead  ? "bg-gray-300 dark:bg-gray-600" : "bg-primary"
          } `}
        />
      </div>
      {!p.last ? (
        <div className="border-b border-gray-200 dark:border-gray-700 my-2" />
      ) : (
        ""
      )}
    </div>
  );
}

function TextOfType(p:{
  type:AdmUserNotificationsApi$LatestNotificationType
}) {
  switch (p.type) {
    case AdmUserNotificationsApi$LatestNotificationType.AssetAiConversation:
      return (
        <div>
          <span className="font-semibold heading-text">Ai Conversation</span>
          <span> was started by a customer</span>
        </div>
      );
    case AdmUserNotificationsApi$LatestNotificationType.AssetVideoCall:
      return (
        <div>
          <span className="font-semibold heading-text">Video Support</span>
          <span> was requested by a customer</span>
        </div>
      );
    case AdmUserNotificationsApi$LatestNotificationType.ProductRegistered:
      return (
        <div>
          <span className="font-semibold heading-text">
            Product Registration
          </span>
          <span> was submitted by a customer</span>
        </div>
      );
    case AdmUserNotificationsApi$LatestNotificationType.Inquiry:
      return (
        <div>
          <span className="font-semibold heading-text">Inquiry</span>
          <span> was submitted by a customer</span>
        </div>
      );
  }
}

const Notification = withHeaderItem(_Notification);

export default Notification;
